import React from 'react';
import { Card } from '../../../components/Card/Card';
import { Delivery } from '../../../model/delivery';
import { Order } from '../../../model/order';
import { fullUserName, User } from '../../../model/user';
import { formatDateTime, formatCurrency } from '../../../util/util';
import { NavLink } from 'react-router-dom';
import { orderPath } from '../../../routes';
import { Icon } from '../../../components/Icon/Icon';
import { SortableList } from '../../../components/SortableList/SortableList';
import { FormattedAddress } from '../../../components/Address/Address';
import { TableBlock } from '../../../components/TableBlock/TableBlock';
import { t } from '../../../locales/translate';

interface Props {
  delivery: Delivery & { order: Order };
}

export function FulfillmentOrderDetails({ delivery }: Props): JSX.Element {
  const buyerName = fullUserName(delivery.address as User);

  return (
    <Card
      title={`${delivery.reference} - ${formatDateTime(delivery.createdAt)}${buyerName ? ` - ${buyerName}` : ''}`}
      actions={
        delivery.orderId && (
          <NavLink to={orderPath(delivery.orderId)} target="_blank">
            <Icon name="external-link" />
          </NavLink>
        )
      }
      fullbleed
    >
      <SortableList
        className="fulfillment-details-table"
        items={[delivery].map((item) => {
          return {
            ...item,
            invoices: item.order ? item.order.invoices : [],
            payments: item.order ? item.order.payments : [],
          };
        })}
        columns={[
          {
            dataKey: 'address',
            label: (
              <div>
                {t.model.delivery.delivery}
                {delivery.documentUrl ? (
                  <a href={delivery.documentUrl} target="_blank" rel="noopener noreferrer">
                    {' '}
                    <Icon name="printer" />
                  </a>
                ) : null}
              </div>
            ),
            cellRenderer: (row) => {
              return (
                <>
                  <FormattedAddress address={row.address} />
                  <div className="fulfillment-details-table__carrier">
                    <strong>{t.model.delivery.carrier}: </strong>
                    <span>{row.shippingMethodId}</span>
                  </div>
                </>
              );
            },
          },
          {
            dataKey: 'invoices',
            label: t.model.invoice._name,
            cellRenderer: (row) => {
              const lastInvoice = row.invoices[row.invoices.length - 1];
              return (
                <>
                  {lastInvoice && <FormattedAddress address={lastInvoice.address} />}
                  {row.invoices.length > 0 && (
                    <div className="fulfillment-details-table__invoices">
                      {row.invoices.map((item) => {
                        return (
                          <a href={item.documentUrl} target="blank" className="button button--link" key={item.id}>
                            {item.reference} - {formatDateTime(item.createdAt)}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            },
          },
          {
            dataKey: 'payments',
            label: t.model.payment._plural,
            cellRenderer: (row) => {
              return row.payments.map((item) => {
                return (
                  <TableBlock
                    key={item.id}
                    header={formatDateTime(item.paidAt)}
                    footer={`${formatCurrency(item.amount)}, ${item.paymentMethod}`}
                  />
                );
              });
            },
          },
          {
            dataKey: 'order',
            label: t.model.order._name,
            cellRenderer: (row) => {
              if (!row.order) {
                return null;
              }
              return <TableBlock header={row.order.reference} footer={formatDateTime(row.order.createdAt)} />;
            },
          },
        ]}
      />
    </Card>
  );
}
